import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Seo } from "@/components/seo/Seo";
import { useI18n } from "@/i18n/I18nProvider";

type Check = {
  name: string;
  status: "ok" | "degraded" | "down" | "unknown";
  details?: string;
};

const Status = () => {
  const { t, locale } = useI18n();
  const [checks, setChecks] = useState<Check[]>([]);

  useEffect(() => {
    const runChecks = async () => {
      const results: Check[] = [];
      const supabaseUrl = (import.meta.env.VITE_SUPABASE_URL || import.meta.env.SUPABASE_URL) as string | undefined;
      if (!supabaseUrl) {
        results.push({ name: "Supabase URL", status: "down", details: "VITE_SUPABASE_URL or SUPABASE_URL missing" });
      } else {
        try {
          const anon =
            (import.meta.env.VITE_SUPABASE_ANON_KEY || import.meta.env.SUPABASE_ANON_KEY) as string | undefined;
          const res = await fetch(`${supabaseUrl}/auth/v1/settings`, {
            method: "GET",
            headers: anon ? { apikey: anon } : {},
          });
          results.push({ name: "Supabase Auth API", status: res.ok ? "ok" : "degraded", details: `HTTP ${res.status}` });
        } catch (e: any) {
          results.push({ name: "Supabase Auth API", status: "down", details: e?.message || "network error" });
        }
      }

      try {
        const { data, error } = await (supabase as any).rpc('health_check');
        results.push({ name: "Supabase RPC health_check", status: error ? "degraded" : (data === 'ok' ? "ok" : "degraded"), details: error ? error.message : String(data) });
      } catch (e: any) {
        results.push({ name: "Supabase RPC health_check", status: "down", details: e?.message || "rpc error" });
      }

      const sentryDsn = import.meta.env.VITE_SENTRY_DSN as string | undefined;
      results.push({ name: "Sentry DSN", status: sentryDsn ? "ok" : "unknown", details: sentryDsn ? "configured" : "not set" });

      setChecks(results);
    };
    runChecks();
  }, []);

  const statusLabel = (s: Check["status"]) => {
    switch (s) {
      case "ok":
        return t("status.badge.ok", "ok");
      case "degraded":
        return t("status.badge.degraded", "degraded");
      case "down":
        return t("status.badge.down", "down");
      default:
        return t("status.badge.unknown", "unknown");
    }
  };

  return (
    <div className="container mx-auto px-6 py-12">
      <Seo
        title={t("status.seo_title", "Status | Halomora")}
        description={t("status.seo_desc", "Halomora system & service status.")}
        canonicalPath="/status"
      />
      <h1 className="text-3xl font-bold text-gray-900 mb-6">{t("status.heading", "System Status")}</h1>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {checks.map((c, idx) => (
          <div key={idx} className="rounded-2xl border border-gray-200 p-4">
            <div className="flex items-center justify-between">
              <div className="text-gray-900 font-medium">{c.name}</div>
              <div className={`text-xs px-2 py-1 rounded-full ${
                c.status === "ok" ? "bg-green-100 text-green-700" :
                c.status === "degraded" ? "bg-yellow-100 text-yellow-700" :
                c.status === "down" ? "bg-red-100 text-red-700" : "bg-gray-100 text-gray-700"
              }`}>
                {statusLabel(c.status)}
              </div>
            </div>
            {c.details && <div className="text-xs text-gray-600 mt-2">{c.details}</div>}
          </div>
        ))}
      </div>
      <div className="text-sm text-gray-600 mt-8">
        {t("status.env_label", "Env")}: {import.meta.env.MODE} | {t("status.build_time", "Build time")}: {new Date().toLocaleString(locale)}
      </div>
    </div>
  );
};

export default Status;
